import React, { useEffect, useMemo, useState } from 'react';
import { ArrowDown, ArrowUp, ArrowUpDown, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from './ui/button';

/*
 * Client-side search + sort + pagination for tables whose data is already loaded.
 *
 *   const t = useTableControls(rows, {
 *     searchKeys: ['sku_code', 'product_name'],   // fields matched by t.query
 *     initialSort: { key: 'days_cover', dir: 'asc' },
 *     pageSize: 50,
 *     sortValue: { brand: (r) => r.brand?.toLowerCase() },  // optional per-key accessors
 *   });
 *   <Input value={t.query} onChange={(e) => t.setQuery(e.target.value)} />
 *   <SortHeader label="Days cover" sortKey="days_cover" table={t} />
 *   {t.pageRows.map(...)}
 *   <TablePager table={t} itemLabel="SKUs" />
 */
export const useTableControls = (rows, { searchKeys = [], initialSort = null, pageSize = 50, sortValue = {} } = {}) => {
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState(initialSort);
  const [page, setPage] = useState(1);

  const filtered = useMemo(() => {
    const list = rows || [];
    const q = query.trim().toLowerCase();
    if (!q || !searchKeys.length) return list;
    return list.filter((row) => searchKeys.some((k) => String(row?.[k] ?? '').toLowerCase().includes(q)));
  }, [rows, query, searchKeys]);

  const sorted = useMemo(() => {
    if (!sort?.key) return filtered;
    const get = sortValue[sort.key] || ((row) => row?.[sort.key]);
    const dir = sort.dir === 'desc' ? -1 : 1;
    return [...filtered].sort((a, b) => {
      const va = get(a);
      const vb = get(b);
      if (va == null && vb == null) return 0;
      if (va == null) return 1; // blanks last either way
      if (vb == null) return -1;
      if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * dir;
      return String(va).localeCompare(String(vb), 'en-IN', { numeric: true }) * dir;
    });
  }, [filtered, sort, sortValue]);

  const pageCount = Math.max(1, Math.ceil(sorted.length / pageSize));
  useEffect(() => { setPage(1); }, [query, sort]);
  useEffect(() => { if (page > pageCount) setPage(pageCount); }, [page, pageCount]);

  const pageRows = useMemo(() => sorted.slice((page - 1) * pageSize, page * pageSize), [sorted, page, pageSize]);
  const toggleSort = (key) => setSort((s) => (s?.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }));

  return { query, setQuery, sort, setSort, toggleSort, page, setPage, pageCount, pageSize, pageRows, total: sorted.length, all: sorted };
};

/** Sortable <th>. Announces direction to screen readers via aria-sort. */
export const SortHeader = ({ label, sortKey, table, className = '', align = 'left' }) => {
  const active = table.sort?.key === sortKey;
  const Icon = !active ? ArrowUpDown : table.sort.dir === 'asc' ? ArrowUp : ArrowDown;
  return (
    <th
      scope="col"
      aria-sort={active ? (table.sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
      className={`px-3 py-2 ${align === 'right' ? 'text-right' : 'text-left'} ${className}`}
    >
      <button
        type="button"
        onClick={() => table.toggleSort(sortKey)}
        className={`inline-flex items-center gap-1 font-medium hover:text-gray-900 ${active ? 'text-gray-900' : ''}`}
      >
        {label}
        <Icon className="h-3.5 w-3.5" aria-hidden="true" />
      </button>
    </th>
  );
};

/** "1–50 of 312 SKUs  ‹ Page 1 of 7 ›". Renders nothing when everything fits on one page. */
export const TablePager = ({ table, itemLabel = 'rows', className = '' }) => {
  if (table.total <= table.pageSize) {
    return table.total > 0
      ? <p className={`px-3 py-2 text-xs text-gray-600 ${className}`}>{table.total.toLocaleString('en-IN')} {itemLabel}</p>
      : null;
  }
  const start = (table.page - 1) * table.pageSize + 1;
  const end = Math.min(table.page * table.pageSize, table.total);
  return (
    <div className={`flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-xs text-gray-600 ${className}`}>
      <span>{start.toLocaleString('en-IN')}–{end.toLocaleString('en-IN')} of {table.total.toLocaleString('en-IN')} {itemLabel}</span>
      <div className="flex items-center gap-1">
        <Button size="icon" variant="outline" className="h-7 w-7" aria-label="Previous page"
          disabled={table.page <= 1} onClick={() => table.setPage(table.page - 1)}>
          <ChevronLeft className="h-4 w-4" aria-hidden="true" />
        </Button>
        <span className="px-1">Page {table.page} of {table.pageCount}</span>
        <Button size="icon" variant="outline" className="h-7 w-7" aria-label="Next page"
          disabled={table.page >= table.pageCount} onClick={() => table.setPage(table.page + 1)}>
          <ChevronRight className="h-4 w-4" aria-hidden="true" />
        </Button>
      </div>
    </div>
  );
};
